import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Copy, Check, Share2, Users, Link2, ExternalLink } from "lucide-react";

export default function Share() {
  const { user } = useAuth();
  const [copied, setCopied] = useState("");

  const origin = window.location.origin;
  const joinLink = `${origin}/?ref=${user?.referral_code}`;
  const physicianLink = `${origin}/physician-onboarding?ref=${user?.referral_code}`;

  const copy = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    toast.success("Copied to clipboard!");
    setTimeout(() => setCopied(""), 1800);
  };

  return (
    <div data-testid="share-page">
      <div className="mb-8">
        <p className="text-xs uppercase tracking-[0.2em] text-patriot font-bold">Grow your network</p>
        <h1 className="font-display text-3xl text-navy mt-1">Share &amp; Grow</h1>
        <p className="text-muted-foreground mt-2">Recruit new representatives with your personal referral code.</p>
      </div>

      <div className="bg-navy-deep text-white rounded-xl p-8 mb-6 afha-grain">
        <p className="text-white/60 text-sm">Your referral code</p>
        <p className="font-display text-4xl font-black tracking-wider mt-1" data-testid="referral-code">{user?.referral_code}</p>
      </div>

      <div className="grid gap-5">
        <div className="bg-white border border-border rounded-xl p-6">
          <div className="flex items-center gap-2 mb-3">
            <Link2 className="h-5 w-5 text-navy" />
            <h2 className="font-display text-lg text-navy">Recruitment link</h2>
          </div>
          <p className="text-sm text-muted-foreground mb-4">Share this link. It opens the AFHA home page, and anyone who signs up joins your team automatically.</p>
          <div className="flex gap-2">
            <Input readOnly value={joinLink} data-testid="join-link" className="font-mono text-sm" />
            <Button onClick={() => copy(joinLink, "join")} data-testid="copy-join-link" className="rounded-full bg-navy hover:bg-navy-dark text-white gap-2 shrink-0">
              {copied === "join" ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />} Copy
            </Button>
          </div>
        </div>

        <div className="bg-white border border-border rounded-xl p-6">
          <div className="flex items-center gap-2 mb-3">
            <Users className="h-5 w-5 text-navy" />
            <h2 className="font-display text-lg text-navy">Physician onboarding link</h2>
          </div>
          <p className="text-sm text-muted-foreground mb-4">Send this to doctors. It opens the Physician Onboarding page, and any practice that enrolls is credited to you.</p>
          <div className="flex gap-2">
            <Input readOnly value={physicianLink} data-testid="profile-link" className="font-mono text-sm" />
            <Button onClick={() => copy(physicianLink, "physician")} variant="outline" data-testid="copy-profile-link" className="rounded-full border-navy text-navy hover:bg-navy hover:text-white gap-2 shrink-0">
              {copied === "physician" ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />} Copy
            </Button>
            <Button onClick={() => window.open(physicianLink, "_blank")} variant="ghost" className="rounded-full text-navy shrink-0" data-testid="open-profile">
              <ExternalLink className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
